import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAutoEVSettings, updateAutoEVSettings } from "./service";
import i18n from "../../../utilities/i18n";

const isOk = (status) => status === 200 || status === 201;

const defaultSettings = {
    is_enabled: false,
    service_type_code: "30",
    skipped_payer_ids: [],
    payer_options: [],
    exception_claim_status_id: null,
    exception_status_options: [],
};

export const fetchAutoEVSettings = createAsyncThunk(
    "autoEV/fetchSettings",
    async (practicePk, { rejectWithValue }) => {
        try {
            const response = await getAutoEVSettings(practicePk);
            if (!isOk(response?.status)) {
                return rejectWithValue({
                    message:
                        response?.data?.message ||
                        i18n.t("errorMessages.commonError"),
                    status: response?.status,
                });
            }
            return response.data;
        } catch (error) {
            return rejectWithValue({
                message: error?.message || i18n.t("errorMessages.commonError"),
            });
        }
    }
);

export const saveAutoEVSettings = createAsyncThunk(
    "autoEV/saveSettings",
    async ({ practicePk, data }, { rejectWithValue }) => {
        try {
            const response = await updateAutoEVSettings(practicePk, data);
            if (!isOk(response?.status)) {
                const details = response?.data?.data || {};
                const enabledErr = details?.is_enabled;
                const enabledText = Array.isArray(enabledErr)
                    ? enabledErr.join(" ")
                    : enabledErr;
                return rejectWithValue({
                    message:
                        enabledText ||
                        response?.data?.message ||
                        i18n.t("automationConfig.autoEV.saveFailure"),
                });
            }
            return response.data;
        } catch (error) {
            return rejectWithValue({
                message:
                    error?.message ||
                    i18n.t("automationConfig.autoEV.saveFailure"),
            });
        }
    }
);

const autoEVSlice = createSlice({
    name: "autoEVAutomation",
    initialState: {
        settings: defaultSettings,
        loading: false,
        saving: false,
        settingsLoaded: false,
        accessDenied: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAutoEVSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAutoEVSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.settingsLoaded = true;
                state.accessDenied = false;
                state.settings = { ...defaultSettings, ...action.payload };
            })
            .addCase(fetchAutoEVSettings.rejected, (state, action) => {
                state.loading = false;
                state.settingsLoaded = true;
                state.accessDenied =
                    action.payload?.status === 403 ||
                    action.payload?.status === 401;
                state.error = action.payload?.message;
            })
            .addCase(saveAutoEVSettings.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(saveAutoEVSettings.fulfilled, (state, action) => {
                state.saving = false;
                state.settings = { ...defaultSettings, ...action.payload };
            })
            .addCase(saveAutoEVSettings.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload?.message;
            });
    },
});

export default autoEVSlice.reducer;
